/**
 * The venues this chain's token is followed on.
 *
 * One today, NEAR Intents. Asked of the API rather than written here, so a
 * chain followed nowhere says so instead of offering a link to an empty page,
 * and a second venue appears without the frontend learning about it first.
 * blackbeard/observer#32.
 */

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import type { Markets } from '../api/generated/Markets'
import { RequestFailed, fetchMarkets } from '../api/rest'
import { href } from '../lib/routes'

export function MarketsIndex({ chain }: { chain: string }) {
  const [markets, setMarkets] = useState<Markets | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    setMarkets(null)
    setError(null)
    fetchMarkets(chain, controller.signal)
      .then(setMarkets)
      .catch((e: unknown) => {
        if (controller.signal.aborted) return
        setError(e instanceof RequestFailed ? e.message : 'Could not reach the observer.')
      })
    return () => controller.abort()
  }, [chain])

  return (
    <section className="panel" style={{ marginBottom: 26 }}>
      <div className="panel-head">
        <h2 className="panel-title">Markets</h2>
        <span className="eyebrow">where this chain&apos;s token is followed</span>
      </div>
      {error && <p className="empty">{error}</p>}
      {!error && !markets && <p className="empty">Reading…</p>}
      {markets && markets.markets.length === 0 && (
        <p className="empty">This chain&apos;s token is not followed on any market.</p>
      )}
      {markets && markets.markets.length > 0 && (
        <div className="scroll-x">
          <table className="board">
            <caption className="visually-hidden">
              Markets this chain&apos;s token is followed on
            </caption>
            <thead>
              <tr>
                <th scope="col" className="left">
                  Market
                </th>
                <th scope="col" className="left">
                  Asset there
                </th>
                <th scope="col" className="left">
                  How a price is read
                </th>
              </tr>
            </thead>
            <tbody>
              {markets.markets.map((m) => (
                <tr key={m.id}>
                  <td className="left">
                    <Link to={href({ chain, market: m.id })}>{m.name}</Link>
                  </td>
                  <td className="left" title={m.asset}>
                    {m.asset_symbol ?? m.asset}
                  </td>
                  <td className="left">{m.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="panel-note">
        A market here is a place where trades of this token settle and can be read back. A quote
        from a front end, or a rate on a swap screen, is not one: it is an offer, and nothing
        settled at it.
      </p>
    </section>
  )
}
